/**
 * TUG OF WAR ASSETS & AUDIO SYNTH ENGINE (HU-06)
 * Biblioteca de recursos gráficos, fallbacks SVG vectoriales inmutables y sintetizador Web Audio FX
 * para la Pantalla Gigante de Estadio (/screen) de Estira y Afloja (Tug Of War).
 * 
 * Proporciona avatares hiper-estilizados de tracción en 3 fases, poses de victoria/derrota,
 * textura de cuerda física, pañuelo marcador central, charco de lodo y síntesis procedural de audio
 * a 60 FPS sin dependencias de archivos de audio externos pesados.
 * 
 * @module components/Screen/Sections/TugOfWar/TugOfWarAssets
 */

/**
 * Resuelve una URL de recurso multimedia relativa (ej. /uploads/tugofwar/rope.png)
 * anteponiendo el origen del backend API si no es una URL absoluta o Data URI/Blob.
 * Sanitiza contra esquemas peligrosos como javascript: o vbscript:.
 * 
 * @param {string|null} url - Ruta del recurso proporcionada por la base de datos o el tema.
 * @param {string} [fallback=''] - Recurso de respaldo en caso de que la URL sea nula o vacía.
 * @returns {string} URL normalizada y lista para renderizado en el navegador.
 */
export const resolveAssetUrl = (url, fallback = '') => {
    if (!url || typeof url !== 'string') return fallback || '';
    const cleanUrl = url.trim();
    if (!cleanUrl) return fallback || '';

    // Bloqueo estricto de esquemas peligrosos
    if (/^(javascript|vbscript|data:(?!image\/))/i.test(cleanUrl)) {
        return fallback || '';
    }

    if (
        cleanUrl.startsWith('http://') ||
        cleanUrl.startsWith('https://') ||
        cleanUrl.startsWith('data:image/') ||
        cleanUrl.startsWith('blob:') ||
        cleanUrl.startsWith('//')
    ) {
        return cleanUrl;
    }

    const apiBase = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';
    const backendOrigin = apiBase.replace(/\/api\/?$/i, '').replace(/\/+$/, '');
    const normalizedPath = cleanUrl.startsWith('/') ? cleanUrl : `/${cleanUrl}`;
    return `${backendOrigin}${normalizedPath}`;
};

export const getResolvedMediaUrl = resolveAssetUrl;

/**
 * Precarga de forma asíncrona un conjunto de URLs de imágenes en la memoria RAM del navegador
 * para asegurar transiciones instantáneas a 60 FPS sin parpadeos ni tirones visuales.
 * 
 * @param {Array<string>} [urls=[]] - Colección de URLs o Data URIs a precargar.
 * @returns {Promise<Array<{url: string, success: boolean}>>} Promesa resuelta con el estado de carga de cada recurso.
 */
export const preloadImageUrls = (urls = []) => {
    if (!Array.isArray(urls)) return Promise.resolve([]);
    const cleanUrls = urls
        .filter((u) => typeof u === 'string' && u.trim().length > 0)
        .map((u) => resolveAssetUrl(u))
        .filter((u) => u && !u.startsWith('data:'));

    if (cleanUrls.length === 0) return Promise.resolve([]);

    const loadPromises = cleanUrls.map((url) => {
        return new Promise((resolve) => {
            const img = new Image();
            let settled = false;

            const onSettled = (success) => {
                if (settled) return;
                settled = true;
                if (timeoutId) clearTimeout(timeoutId);
                img.onload = null;
                img.onerror = null;
                resolve({ url, success });
            };

            const timeoutId = setTimeout(() => onSettled(false), 8000);

            img.onload = () => onSettled(true);
            img.onerror = () => onSettled(false);
            img.src = url;
        });
    });

    return Promise.all(loadPromises);
};

// =========================================================================
// FALLBACKS SVG VECTORIALES INMUTABLES (ZERO-CANVAS 60 FPS)
// =========================================================================

/**
 * Textura de Cuerda Trenzada Horizontal por defecto (1200x80 px).
 * Renderizada con gradientes de fibras de cáñamo natural y relieve 3D.
 * @type {string}
 */
export const DEFAULT_ROPE_SVG = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 80" width="1200" height="80">
  <defs>
    <linearGradient id="ropeGrad" x1="0%" y1="0%" x2="0%" y2="100%">
      <stop offset="0%" stop-color="%23d4a373" />
      <stop offset="30%" stop-color="%23fefae0" />
      <stop offset="60%" stop-color="%23bc6c25" />
      <stop offset="100%" stop-color="%23603813" />
    </linearGradient>
    <pattern id="ropeBraids" width="40" height="80" patternUnits="userSpaceOnUse">
      <path d="M0 0 Q20 40 0 80 M20 0 Q40 40 20 80 M40 0 Q60 40 40 80" fill="none" stroke="%23582f0e" stroke-width="4" opacity="0.6"/>
      <ellipse cx="10" cy="40" rx="9" ry="34" fill="url(%23ropeGrad)" transform="rotate(25 10 40)" stroke="%237f4f24" stroke-width="2"/>
      <ellipse cx="30" cy="40" rx="9" ry="34" fill="url(%23ropeGrad)" transform="rotate(-25 30 40)" stroke="%237f4f24" stroke-width="2"/>
    </pattern>
    <filter id="ropeShadow" x="-5%" y="-20%" width="110%" height="150%">
      <feDropShadow dx="0" dy="8" stdDeviation="6" flood-color="%23000000" flood-opacity="0.7"/>
    </filter>
  </defs>
  <rect x="0" y="10" width="1200" height="60" rx="20" fill="url(%23ropeBraids)" filter="url(%23ropeShadow)" stroke="%23432818" stroke-width="3"/>
  <line x1="0" y1="20" x2="1200" y2="20" stroke="%23fff" stroke-width="1.5" opacity="0.3"/>
</svg>`;

/**
 * Pañuelo / Listón Rojo Marcador Central (#flag) atado a la cuerda (120x160 px).
 * Con pliegues de tela y nudo central de alta visibilidad.
 * @type {string}
 */
export const DEFAULT_ROPE_FLAG_SVG = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 160" width="120" height="160">
  <defs>
    <linearGradient id="flagGrad" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="%23ff1744" />
      <stop offset="50%" stop-color="%23d50000" />
      <stop offset="100%" stop-color="%23880e4f" />
    </linearGradient>
    <filter id="flagGlow" x="-20%" y="-20%" width="140%" height="140%">
      <feDropShadow dx="0" dy="6" stdDeviation="8" flood-color="%23ff1744" flood-opacity="0.8"/>
    </filter>
  </defs>
  <!-- Nudo Central sobre la cuerda -->
  <ellipse cx="60" cy="30" rx="26" ry="18" fill="%23d50000" stroke="%23ffffff" stroke-width="3" filter="url(%23flagGlow)"/>
  <ellipse cx="60" cy="30" rx="14" ry="9" fill="%23ff5252"/>
  <!-- Tela colgante en punta -->
  <path d="M42 38 Q35 90 20 150 Q60 135 100 150 Q85 90 78 38 Z" fill="url(%23flagGrad)" stroke="%23ffffff" stroke-width="3" filter="url(%23flagGlow)"/>
  <!-- Pliegues y sombras -->
  <path d="M60 40 Q55 90 50 142" stroke="%23ff8a80" stroke-width="2.5" fill="none" opacity="0.8"/>
  <path d="M72 40 Q68 90 75 145" stroke="%234a0e17" stroke-width="2.5" fill="none" opacity="0.6"/>
</svg>`;

/**
 * Charco de Lodo Central por defecto (600x200 px).
 * Renderizado con curvas fluidas, reflejos viscosos y salpicaduras periféricas.
 * @type {string}
 */
export const DEFAULT_MUD_PUDDLE_SVG = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 600 200" width="600" height="200">
  <defs>
    <radialGradient id="mudDeep" cx="50%" cy="50%" r="50%">
      <stop offset="0%" stop-color="%232b1704" stop-opacity="0.95" />
      <stop offset="60%" stop-color="%233e2107" stop-opacity="0.9" />
      <stop offset="85%" stop-color="%235a320c" stop-opacity="0.85" />
      <stop offset="100%" stop-color="%231a0c02" stop-opacity="0.2" />
    </radialGradient>
    <linearGradient id="mudShine" x1="0%" y1="0%" x2="100%" y2="0%">
      <stop offset="0%" stop-color="%238c531b" stop-opacity="0" />
      <stop offset="50%" stop-color="%23c48b47" stop-opacity="0.6" />
      <stop offset="100%" stop-color="%238c531b" stop-opacity="0" />
    </linearGradient>
    <filter id="mudBlur">
      <feGaussianBlur stdDeviation="3" />
    </filter>
  </defs>
  <!-- Sombra exterior difusa -->
  <ellipse cx="300" cy="115" rx="270" ry="70" fill="%230b0501" opacity="0.6" filter="url(%23mudBlur)"/>
  <!-- Masa principal del charco -->
  <path d="M50 110 C80 60, 200 40, 300 45 C420 50, 520 70, 550 110 C570 145, 470 175, 300 180 C130 185, 25 150, 50 110 Z" fill="url(%23mudDeep)" stroke="%23432818" stroke-width="4"/>
  <!-- Ondas y reflejos interiores -->
  <ellipse cx="300" cy="110" rx="190" ry="40" fill="none" stroke="url(%23mudShine)" stroke-width="3" opacity="0.7"/>
  <ellipse cx="260" cy="95" rx="100" ry="20" fill="none" stroke="%23b07d48" stroke-width="2" opacity="0.5"/>
  <!-- Salpicaduras en los bordes -->
  <circle cx="90" cy="65" r="8" fill="%233e2107"/>
  <circle cx="120" cy="50" r="5" fill="%235a320c"/>
  <circle cx="510" cy="70" r="9" fill="%233e2107"/>
  <circle cx="480" cy="55" r="6" fill="%235a320c"/>
  <circle cx="310" cy="188" r="7" fill="%232b1704"/>
  <circle cx="180" cy="178" r="6" fill="%233e2107"/>
  <circle cx="430" cy="175" r="8" fill="%233e2107"/>
</svg>`;

/**
 * Avatar Equipo 1 - Fase 1: Tirando con energía (Postura erguida y agarre firme).
 * @type {string}
 */
export const DEFAULT_PLAYER1_PHASE1_SVG = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 400" width="300" height="400">
  <defs>
    <radialGradient id="p1Aura" cx="50%" cy="50%" r="50%">
      <stop offset="0%" stop-color="%2300f0ff" stop-opacity="0.8"/>
      <stop offset="70%" stop-color="%230284c7" stop-opacity="0.3"/>
      <stop offset="100%" stop-color="%230f172a" stop-opacity="0"/>
    </radialGradient>
    <filter id="p1Glow" x="-20%" y="-20%" width="140%" height="140%">
      <feDropShadow dx="0" dy="0" stdDeviation="8" flood-color="%2300f0ff" flood-opacity="0.9"/>
    </filter>
  </defs>
  <!-- Aura de energía -->
  <circle cx="150" cy="200" r="140" fill="url(%23p1Aura)"/>
  <!-- Cabeza con casco deportivo -->
  <circle cx="120" cy="90" r="45" fill="%23082f49" stroke="%2300f0ff" stroke-width="4" filter="url(%23p1Glow)"/>
  <path d="M95 80 Q120 60 145 80" stroke="%2338bdf8" stroke-width="4" fill="none"/>
  <!-- Ojos neón -->
  <polygon points="105,88 120,92 115,100 100,95" fill="%2300f0ff" filter="url(%23p1Glow)"/>
  <!-- Torso inclinado ligeramente hacia la izquierda -->
  <path d="M100 135 L170 170 L140 260 L80 230 Z" fill="%230369a1" stroke="%2300f0ff" stroke-width="4"/>
  <!-- Brazos extendidos sosteniendo la cuerda -->
  <path d="M150 160 L240 180 L230 205 L140 185 Z" fill="%230284c7" stroke="%2338bdf8" stroke-width="3"/>
  <circle cx="240" cy="190" r="16" fill="%2300f0ff" stroke="%23ffffff" stroke-width="3"/>
  <!-- Piernas en postura de apoyo -->
  <path d="M100 245 L60 360 L90 370 L120 255 Z" fill="%23082f49" stroke="%2300f0ff" stroke-width="4"/>
  <path d="M135 250 L180 355 L210 350 L155 245 Z" fill="%230c4a6e" stroke="%2338bdf8" stroke-width="4"/>
  <rect x="40" y="355" width="60" height="25" rx="10" fill="%2300f0ff"/>
  <rect x="175" y="345" width="55" height="25" rx="10" fill="%2300f0ff"/>
  <!-- Número 1 en el pecho -->
  <text x="125" y="205" font-family="Arial, Impact, sans-serif" font-size="32" font-weight="900" fill="%23ffffff" text-anchor="middle">#1</text>
</svg>`;

/**
 * Avatar Equipo 1 - Fase 2: Esfuerzo medio con sudor y tensión visible.
 * @type {string}
 */
export const DEFAULT_PLAYER1_PHASE2_SVG = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 400" width="300" height="400">
  <defs>
    <radialGradient id="p1Aura2" cx="50%" cy="50%" r="50%">
      <stop offset="0%" stop-color="%2300f0ff" stop-opacity="0.9"/>
      <stop offset="60%" stop-color="%230284c7" stop-opacity="0.5"/>
      <stop offset="100%" stop-color="%230f172a" stop-opacity="0"/>
    </radialGradient>
    <filter id="p1Glow2" x="-20%" y="-20%" width="140%" height="140%">
      <feDropShadow dx="0" dy="0" stdDeviation="12" flood-color="%2300f0ff" flood-opacity="1"/>
    </filter>
  </defs>
  <circle cx="130" cy="210" r="145" fill="url(%23p1Aura2)"/>
  <!-- Gotas de sudor volando -->
  <path d="M60 90 Q50 110 55 120 A6 6 0 0 0 65 120 Q70 110 60 90 Z" fill="%2338bdf8" filter="url(%23p1Glow2)"/>
  <path d="M90 60 Q80 75 85 85 A5 5 0 0 0 95 85 Q100 75 90 60 Z" fill="%2338bdf8"/>
  <!-- Cabeza más inclinada -->
  <circle cx="95" cy="110" r="45" fill="%23082f49" stroke="%2300f0ff" stroke-width="5" filter="url(%23p1Glow2)"/>
  <!-- Dientes apretados -->
  <rect x="85" y="125" width="25" height="12" rx="3" fill="%23ffffff" stroke="%2300f0ff" stroke-width="2"/>
  <polygon points="80,105 100,110 95,118 75,112" fill="%2300f0ff"/>
  <!-- Torso más reclinado -->
  <path d="M75 155 L160 185 L125 275 L55 240 Z" fill="%230369a1" stroke="%2300f0ff" stroke-width="4"/>
  <!-- Brazos tensos -->
  <path d="M135 175 L245 190 L235 220 L125 205 Z" fill="%230284c7" stroke="%2338bdf8" stroke-width="4"/>
  <circle cx="245" cy="205" r="18" fill="%2300f0ff" stroke="%23ffffff" stroke-width="3"/>
  <!-- Piernas firmes apoyadas contra el suelo -->
  <path d="M75 255 L30 365 L65 375 L100 265 Z" fill="%23082f49" stroke="%2300f0ff" stroke-width="5"/>
  <path d="M120 265 L175 360 L205 350 L145 255 Z" fill="%230c4a6e" stroke="%2338bdf8" stroke-width="4"/>
  <rect x="15" y="360" width="65" height="26" rx="10" fill="%2300f0ff"/>
  <rect x="165" y="348" width="60" height="26" rx="10" fill="%2300f0ff"/>
  <text x="105" y="225" font-family="Arial, Impact, sans-serif" font-size="32" font-weight="900" fill="%23ffffff" text-anchor="middle">#1</text>
</svg>`;

/**
 * Avatar Equipo 1 - Fase 3: Tensión máxima / Al borde del K.O.
 * @type {string}
 */
export const DEFAULT_PLAYER1_PHASE3_SVG = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 400" width="300" height="400">
  <defs>
    <radialGradient id="p1Aura3" cx="50%" cy="50%" r="50%">
      <stop offset="0%" stop-color="%2338bdf8" stop-opacity="1"/>
      <stop offset="50%" stop-color="%230284c7" stop-opacity="0.6"/>
      <stop offset="100%" stop-color="%230f172a" stop-opacity="0"/>
    </radialGradient>
    <filter id="p1Glow3" x="-20%" y="-20%" width="140%" height="140%">
      <feDropShadow dx="0" dy="0" stdDeviation="15" flood-color="%2300f0ff" flood-opacity="1"/>
    </filter>
  </defs>
  <circle cx="110" cy="230" r="150" fill="url(%23p1Aura3)"/>
  <!-- Rayos de energía por sobreesfuerzo -->
  <polyline points="20,130 50,150 35,175 70,195" fill="none" stroke="%2300f0ff" stroke-width="3" filter="url(%23p1Glow3)"/>
  <polyline points="190,140 220,155 205,180 240,200" fill="none" stroke="%2300f0ff" stroke-width="3" filter="url(%23p1Glow3)"/>
  <!-- Cabeza en ángulo extremo -->
  <circle cx="70" cy="140" r="45" fill="%23082f49" stroke="%2300f0ff" stroke-width="5" filter="url(%23p1Glow3)"/>
  <!-- Expresión de esfuerzo extremo -->
  <polygon points="50,135 75,140 70,150 45,145" fill="%2300f0ff"/>
  <rect x="60" y="158" width="28" height="14" rx="4" fill="%23ffffff" stroke="%23ef4444" stroke-width="2"/>
  <!-- Torso casi horizontal -->
  <path d="M50 185 L145 205 L105 295 L30 265 Z" fill="%230369a1" stroke="%2300f0ff" stroke-width="5"/>
  <!-- Brazos al límite jalando -->
  <path d="M120 195 L250 205 L240 235 L110 225 Z" fill="%230284c7" stroke="%2338bdf8" stroke-width="5"/>
  <circle cx="250" cy="220" r="20" fill="%2300f0ff" stroke="%23ffffff" stroke-width="4"/>
  <!-- Piernas clavadas en la arena -->
  <path d="M50 280 L10 375 L45 385 L85 290 Z" fill="%23082f49" stroke="%2300f0ff" stroke-width="5"/>
  <path d="M100 285 L165 370 L195 360 L125 275 Z" fill="%230c4a6e" stroke="%2338bdf8" stroke-width="4"/>
  <rect x="0" y="370" width="65" height="26" rx="10" fill="%2300f0ff"/>
  <rect x="155" y="358" width="60" height="26" rx="10" fill="%2300f0ff"/>
  <text x="80" y="250" font-family="Arial, Impact, sans-serif" font-size="32" font-weight="900" fill="%23ffffff" text-anchor="middle">#1</text>
</svg>`;

/**
 * Avatar Equipo 1 - Derrota / Caída en el lodo (Loser).
 * @type {string}
 */
export const DEFAULT_PLAYER1_LOSER_SVG = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 400" width="300" height="400">
  <defs>
    <filter id="p1MudSplash">
      <feDropShadow dx="0" dy="4" stdDeviation="6" flood-color="%232b1704" flood-opacity="0.8"/>
    </filter>
  </defs>
  <!-- Charco de lodo cubriendo al personaje -->
  <ellipse cx="150" cy="320" rx="130" ry="50" fill="%233e2107" stroke="%235a320c" stroke-width="4"/>
  <ellipse cx="150" cy="315" rx="90" ry="30" fill="%232b1704"/>
  <!-- Salpicaduras de lodo -->
  <path d="M80 290 Q60 250 50 230 Q70 250 90 280 Z" fill="%235a320c"/>
  <path d="M220 290 Q240 250 250 230 Q230 250 210 280 Z" fill="%235a320c"/>
  <!-- Cabeza mareada sumergida -->
  <circle cx="150" cy="240" r="45" fill="%23082f49" stroke="%2300f0ff" stroke-width="4"/>
  <!-- Ojos en cruz (K.O.) -->
  <text x="135" y="245" font-family="Arial, sans-serif" font-size="28" font-weight="900" fill="%23ef4444" text-anchor="middle">✕</text>
  <text x="165" y="245" font-family="Arial, sans-serif" font-size="28" font-weight="900" fill="%23ef4444" text-anchor="middle">✕</text>
  <!-- Estrellas de mareo flotando -->
  <polygon points="110,160 115,175 130,175 118,185 122,200 110,190 98,200 102,185 90,175 105,175" fill="%23fde047"/>
  <polygon points="190,160 195,175 210,175 198,185 202,200 190,190 178,200 182,185 170,175 185,175" fill="%23fde047"/>
  <!-- Manchas de lodo en el cuerpo -->
  <path d="M120 280 C130 270 170 270 180 280 L190 330 L110 330 Z" fill="%230369a1" stroke="%233e2107" stroke-width="6"/>
  <ellipse cx="150" cy="285" rx="30" ry="12" fill="%233e2107"/>
</svg>`;

/**
 * Avatar Equipo 1 - Victoria / Campeón (Winner con trofeo y puño en alto).
 * @type {string}
 */
export const DEFAULT_PLAYER1_WINNER_SVG = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 400" width="300" height="400">
  <defs>
    <radialGradient id="winAuraP1" cx="50%" cy="50%" r="50%">
      <stop offset="0%" stop-color="%23fde047" stop-opacity="0.9"/>
      <stop offset="60%" stop-color="%2300f0ff" stop-opacity="0.5"/>
      <stop offset="100%" stop-color="%230f172a" stop-opacity="0"/>
    </radialGradient>
    <filter id="winGlowP1" x="-20%" y="-20%" width="140%" height="140%">
      <feDropShadow dx="0" dy="0" stdDeviation="12" flood-color="%23fde047" flood-opacity="0.9"/>
    </filter>
  </defs>
  <!-- Aura dorada de victoria -->
  <circle cx="150" cy="200" r="145" fill="url(%23winAuraP1)"/>
  <!-- Cabeza sonriente con corona -->
  <circle cx="150" cy="120" r="45" fill="%23082f49" stroke="%2300f0ff" stroke-width="4" filter="url(%23winGlowP1)"/>
  <polygon points="125,75 135,55 150,70 165,55 175,75" fill="%23fde047" stroke="%23ffffff" stroke-width="2"/>
  <!-- Sonrisa triunfal -->
  <path d="M135 140 Q150 155 165 140" stroke="%23ffffff" stroke-width="4" fill="none"/>
  <polygon points="135,115 145,120 140,126 130,122" fill="%2300f0ff"/>
  <polygon points="165,115 155,120 160,126 170,122" fill="%2300f0ff"/>
  <!-- Torso erguido con medalla -->
  <path d="M110 165 L190 165 L175 270 L125 270 Z" fill="%230369a1" stroke="%2300f0ff" stroke-width="4"/>
  <circle cx="150" cy="215" r="18" fill="%23fde047" stroke="%23d97706" stroke-width="3" filter="url(%23winGlowP1)"/>
  <text x="150" y="222" font-family="Arial, Impact, sans-serif" font-size="16" font-weight="bold" fill="%2378350f" text-anchor="middle">#1</text>
  <!-- Brazos levantados en señal de victoria -->
  <path d="M115 175 L60 90 L85 75 L130 165 Z" fill="%230284c7" stroke="%2338bdf8" stroke-width="4"/>
  <circle cx="65" cy="80" r="16" fill="%2300f0ff" stroke="%23ffffff" stroke-width="3"/>
  <path d="M185 175 L240 90 L215 75 L170 165 Z" fill="%230284c7" stroke="%2338bdf8" stroke-width="4"/>
  <circle cx="235" cy="80" r="16" fill="%2300f0ff" stroke="%23ffffff" stroke-width="3"/>
  <!-- Piernas firmes -->
  <path d="M125 270 L105 370 L135 375 L145 270 Z" fill="%23082f49" stroke="%2300f0ff" stroke-width="4"/>
  <path d="M175 270 L195 370 L165 375 L155 270 Z" fill="%23082f49" stroke="%2300f0ff" stroke-width="4"/>
  <rect x="85" y="365" width="55" height="24" rx="8" fill="%2300f0ff"/>
  <rect x="160" y="365" width="55" height="24" rx="8" fill="%2300f0ff"/>
</svg>`;

/**
 * Avatar Equipo 2 - Fase 1: Tirando con energía hacia la derecha.
 * @type {string}
 */
export const DEFAULT_PLAYER2_PHASE1_SVG = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 400" width="300" height="400">
  <defs>
    <radialGradient id="p2Aura" cx="50%" cy="50%" r="50%">
      <stop offset="0%" stop-color="%23ff0055" stop-opacity="0.8"/>
      <stop offset="70%" stop-color="%23be123c" stop-opacity="0.3"/>
      <stop offset="100%" stop-color="%231a0510" stop-opacity="0"/>
    </radialGradient>
    <filter id="p2Glow" x="-20%" y="-20%" width="140%" height="140%">
      <feDropShadow dx="0" dy="0" stdDeviation="8" flood-color="%23ff0055" flood-opacity="0.9"/>
    </filter>
  </defs>
  <!-- Aura de energía fucsia -->
  <circle cx="150" cy="200" r="140" fill="url(%23p2Aura)"/>
  <!-- Cabeza con casco deportivo -->
  <circle cx="180" cy="90" r="45" fill="%234c0519" stroke="%23ff0055" stroke-width="4" filter="url(%23p2Glow)"/>
  <path d="M155 80 Q180 60 205 80" stroke="%23fb7185" stroke-width="4" fill="none"/>
  <!-- Ojos neón -->
  <polygon points="195,88 180,92 185,100 200,95" fill="%23ff0055" filter="url(%23p2Glow)"/>
  <!-- Torso inclinado ligeramente hacia la derecha -->
  <path d="M200 135 L130 170 L160 260 L220 230 Z" fill="%23be123c" stroke="%23ff0055" stroke-width="4"/>
  <!-- Brazos extendidos sosteniendo la cuerda hacia la izquierda -->
  <path d="M150 160 L60 180 L70 205 L160 185 Z" fill="%23e11d48" stroke="%23fb7185" stroke-width="3"/>
  <circle cx="60" cy="190" r="16" fill="%23ff0055" stroke="%23ffffff" stroke-width="3"/>
  <!-- Piernas en postura de apoyo -->
  <path d="M200 245 L240 360 L210 370 L180 255 Z" fill="%234c0519" stroke="%23ff0055" stroke-width="4"/>
  <path d="M165 250 L120 355 L90 350 L145 245 Z" fill="%23881337" stroke="%23fb7185" stroke-width="4"/>
  <rect x="200" y="355" width="60" height="25" rx="10" fill="%23ff0055"/>
  <rect x="70" y="345" width="55" height="25" rx="10" fill="%23ff0055"/>
  <!-- Número 2 en el pecho -->
  <text x="175" y="205" font-family="Arial, Impact, sans-serif" font-size="32" font-weight="900" fill="%23ffffff" text-anchor="middle">#2</text>
</svg>`;

/**
 * Avatar Equipo 2 - Fase 2: Esfuerzo medio con sudor y tensión visible.
 * @type {string}
 */
export const DEFAULT_PLAYER2_PHASE2_SVG = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 400" width="300" height="400">
  <defs>
    <radialGradient id="p2Aura2" cx="50%" cy="50%" r="50%">
      <stop offset="0%" stop-color="%23ff0055" stop-opacity="0.9"/>
      <stop offset="60%" stop-color="%23be123c" stop-opacity="0.5"/>
      <stop offset="100%" stop-color="%231a0510" stop-opacity="0"/>
    </radialGradient>
    <filter id="p2Glow2" x="-20%" y="-20%" width="140%" height="140%">
      <feDropShadow dx="0" dy="0" stdDeviation="12" flood-color="%23ff0055" flood-opacity="1"/>
    </filter>
  </defs>
  <circle cx="170" cy="210" r="145" fill="url(%23p2Aura2)"/>
  <!-- Gotas de sudor volando -->
  <path d="M240 90 Q250 110 245 120 A6 6 0 0 1 235 120 Q230 110 240 90 Z" fill="%23fb7185" filter="url(%23p2Glow2)"/>
  <path d="M210 60 Q220 75 215 85 A5 5 0 0 1 205 85 Q200 75 210 60 Z" fill="%23fb7185"/>
  <!-- Cabeza más inclinada -->
  <circle cx="205" cy="110" r="45" fill="%234c0519" stroke="%23ff0055" stroke-width="5" filter="url(%23p2Glow2)"/>
  <!-- Dientes apretados -->
  <rect x="190" y="125" width="25" height="12" rx="3" fill="%23ffffff" stroke="%23ff0055" stroke-width="2"/>
  <polygon points="220,105 200,110 205,118 225,112" fill="%23ff0055"/>
  <!-- Torso más reclinado -->
  <path d="M225 155 L140 185 L175 275 L245 240 Z" fill="%23be123c" stroke="%23ff0055" stroke-width="4"/>
  <!-- Brazos tensos -->
  <path d="M165 175 L55 190 L65 220 L175 205 Z" fill="%23e11d48" stroke="%23fb7185" stroke-width="4"/>
  <circle cx="55" cy="205" r="18" fill="%23ff0055" stroke="%23ffffff" stroke-width="3"/>
  <!-- Piernas firmes apoyadas contra el suelo -->
  <path d="M225 255 L270 365 L235 375 L200 265 Z" fill="%234c0519" stroke="%23ff0055" stroke-width="5"/>
  <path d="M180 265 L125 360 L95 350 L155 255 Z" fill="%23881337" stroke="%23fb7185" stroke-width="4"/>
  <rect x="220" y="360" width="65" height="26" rx="10" fill="%23ff0055"/>
  <rect x="75" y="348" width="60" height="26" rx="10" fill="%23ff0055"/>
  <text x="195" y="225" font-family="Arial, Impact, sans-serif" font-size="32" font-weight="900" fill="%23ffffff" text-anchor="middle">#2</text>
</svg>`;

/**
 * Avatar Equipo 2 - Fase 3: Tensión máxima / Al borde del K.O.
 * @type {string}
 */
export const DEFAULT_PLAYER2_PHASE3_SVG = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 400" width="300" height="400">
  <defs>
    <radialGradient id="p2Aura3" cx="50%" cy="50%" r="50%">
      <stop offset="0%" stop-color="%23ff0055" stop-opacity="1"/>
      <stop offset="50%" stop-color="%23be123c" stop-opacity="0.6"/>
      <stop offset="100%" stop-color="%231a0510" stop-opacity="0"/>
    </radialGradient>
    <filter id="p2Glow3" x="-20%" y="-20%" width="140%" height="140%">
      <feDropShadow dx="0" dy="0" stdDeviation="15" flood-color="%23ff0055" flood-opacity="1"/>
    </filter>
  </defs>
  <circle cx="190" cy="230" r="150" fill="url(%23p2Aura3)"/>
  <!-- Rayos de energía por sobreesfuerzo -->
  <polyline points="280,130 250,150 265,175 230,195" fill="none" stroke="%23ff0055" stroke-width="3" filter="url(%23p2Glow3)"/>
  <polyline points="110,140 80,155 95,180 60,200" fill="none" stroke="%23ff0055" stroke-width="3" filter="url(%23p2Glow3)"/>
  <!-- Cabeza en ángulo extremo -->
  <circle cx="230" cy="140" r="45" fill="%234c0519" stroke="%23ff0055" stroke-width="5" filter="url(%23p2Glow3)"/>
  <!-- Expresión de esfuerzo extremo -->
  <polygon points="250,135 225,140 230,150 255,145" fill="%23ff0055"/>
  <rect x="212" y="158" width="28" height="14" rx="4" fill="%23ffffff" stroke="%23ef4444" stroke-width="2"/>
  <!-- Torso casi horizontal -->
  <path d="M250 185 L155 205 L195 295 L270 265 Z" fill="%23be123c" stroke="%23ff0055" stroke-width="5"/>
  <!-- Brazos al límite jalando -->
  <path d="M180 195 L50 205 L60 235 L190 225 Z" fill="%23e11d48" stroke="%23fb7185" stroke-width="5"/>
  <circle cx="50" cy="220" r="20" fill="%23ff0055" stroke="%23ffffff" stroke-width="4"/>
  <!-- Piernas clavadas en la arena -->
  <path d="M250 280 L290 375 L255 385 L215 290 Z" fill="%234c0519" stroke="%23ff0055" stroke-width="5"/>
  <path d="M200 285 L135 370 L105 360 L175 275 Z" fill="%23881337" stroke="%23fb7185" stroke-width="4"/>
  <rect x="235" y="370" width="65" height="26" rx="10" fill="%23ff0055"/>
  <rect x="85" y="358" width="60" height="26" rx="10" fill="%23ff0055"/>
  <text x="220" y="250" font-family="Arial, Impact, sans-serif" font-size="32" font-weight="900" fill="%23ffffff" text-anchor="middle">#2</text>
</svg>`;

/**
 * Avatar Equipo 2 - Derrota / Caída en el lodo (Loser).
 * @type {string}
 */
export const DEFAULT_PLAYER2_LOSER_SVG = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 400" width="300" height="400">
  <defs>
    <filter id="p2MudSplash">
      <feDropShadow dx="0" dy="4" stdDeviation="6" flood-color="%232b1704" flood-opacity="0.8"/>
    </filter>
  </defs>
  <!-- Charco de lodo cubriendo al personaje -->
  <ellipse cx="150" cy="320" rx="130" ry="50" fill="%233e2107" stroke="%235a320c" stroke-width="4"/>
  <ellipse cx="150" cy="315" rx="90" ry="30" fill="%232b1704"/>
  <!-- Salpicaduras de lodo -->
  <path d="M80 290 Q60 250 50 230 Q70 250 90 280 Z" fill="%235a320c"/>
  <path d="M220 290 Q240 250 250 230 Q230 250 210 280 Z" fill="%235a320c"/>
  <!-- Cabeza mareada sumergida -->
  <circle cx="150" cy="240" r="45" fill="%234c0519" stroke="%23ff0055" stroke-width="4"/>
  <!-- Ojos en cruz (K.O.) -->
  <text x="135" y="245" font-family="Arial, sans-serif" font-size="28" font-weight="900" fill="%23ef4444" text-anchor="middle">✕</text>
  <text x="165" y="245" font-family="Arial, sans-serif" font-size="28" font-weight="900" fill="%23ef4444" text-anchor="middle">✕</text>
  <!-- Estrellas de mareo flotando -->
  <polygon points="110,160 115,175 130,175 118,185 122,200 110,190 98,200 102,185 90,175 105,175" fill="%23fde047"/>
  <polygon points="190,160 195,175 210,175 198,185 202,200 190,190 178,200 182,185 170,175 185,175" fill="%23fde047"/>
  <!-- Manchas de lodo en el cuerpo -->
  <path d="M120 280 C130 270 170 270 180 280 L190 330 L110 330 Z" fill="%23be123c" stroke="%233e2107" stroke-width="6"/>
  <ellipse cx="150" cy="285" rx="30" ry="12" fill="%233e2107"/>
</svg>`;

/**
 * Avatar Equipo 2 - Victoria / Campeón (Winner con trofeo y puño en alto).
 * @type {string}
 */
export const DEFAULT_PLAYER2_WINNER_SVG = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 400" width="300" height="400">
  <defs>
    <radialGradient id="winAuraP2" cx="50%" cy="50%" r="50%">
      <stop offset="0%" stop-color="%23fde047" stop-opacity="0.9"/>
      <stop offset="60%" stop-color="%23ff0055" stop-opacity="0.5"/>
      <stop offset="100%" stop-color="%231a0510" stop-opacity="0"/>
    </radialGradient>
    <filter id="winGlowP2" x="-20%" y="-20%" width="140%" height="140%">
      <feDropShadow dx="0" dy="0" stdDeviation="12" flood-color="%23fde047" flood-opacity="0.9"/>
    </filter>
  </defs>
  <!-- Aura dorada de victoria -->
  <circle cx="150" cy="200" r="145" fill="url(%23winAuraP2)"/>
  <!-- Cabeza sonriente con corona -->
  <circle cx="150" cy="120" r="45" fill="%234c0519" stroke="%23ff0055" stroke-width="4" filter="url(%23winGlowP2)"/>
  <polygon points="125,75 135,55 150,70 165,55 175,75" fill="%23fde047" stroke="%23ffffff" stroke-width="2"/>
  <!-- Sonrisa triunfal -->
  <path d="M135 140 Q150 155 165 140" stroke="%23ffffff" stroke-width="4" fill="none"/>
  <polygon points="135,115 145,120 140,126 130,122" fill="%23ff0055"/>
  <polygon points="165,115 155,120 160,126 170,122" fill="%23ff0055"/>
  <!-- Torso erguido con medalla -->
  <path d="M110 165 L190 165 L175 270 L125 270 Z" fill="%23be123c" stroke="%23ff0055" stroke-width="4"/>
  <circle cx="150" cy="215" r="18" fill="%23fde047" stroke="%23d97706" stroke-width="3" filter="url(%23winGlowP2)"/>
  <text x="150" y="222" font-family="Arial, Impact, sans-serif" font-size="16" font-weight="bold" fill="%2378350f" text-anchor="middle">#2</text>
  <!-- Brazos levantados en señal de victoria -->
  <path d="M115 175 L60 90 L85 75 L130 165 Z" fill="%23e11d48" stroke="%23fb7185" stroke-width="4"/>
  <circle cx="65" cy="80" r="16" fill="%23ff0055" stroke="%23ffffff" stroke-width="3"/>
  <path d="M185 175 L240 90 L215 75 L170 165 Z" fill="%23e11d48" stroke="%23fb7185" stroke-width="4"/>
  <circle cx="235" cy="80" r="16" fill="%23ff0055" stroke="%23ffffff" stroke-width="3"/>
  <!-- Piernas firmes -->
  <path d="M125 270 L105 370 L135 375 L145 270 Z" fill="%234c0519" stroke="%23ff0055" stroke-width="4"/>
  <path d="M175 270 L195 370 L165 375 L155 270 Z" fill="%234c0519" stroke="%23ff0055" stroke-width="4"/>
  <rect x="85" y="365" width="55" height="24" rx="8" fill="%23ff0055"/>
  <rect x="160" y="365" width="55" height="24" rx="8" fill="%23ff0055"/>
</svg>`;

/**
 * Fondo de Estadio por defecto 1920x1080 (Gradas, reflectores y arena central).
 * @type {string}
 */
export const DEFAULT_TUG_BG_SVG = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="1920" height="1080" viewBox="0 0 1920 1080">
  <defs>
    <radialGradient id="stadiumSky" cx="50%" cy="30%" r="80%">
      <stop offset="0%" stop-color="%231e1b4b" />
      <stop offset="60%" stop-color="%230f172a" />
      <stop offset="100%" stop-color="%23020617" />
    </radialGradient>
    <linearGradient id="laserBeamL" x1="0%" y1="0%" x2="60%" y2="100%">
      <stop offset="0%" stop-color="%2300f0ff" stop-opacity="0.35" />
      <stop offset="100%" stop-color="%2300f0ff" stop-opacity="0" />
    </linearGradient>
    <linearGradient id="laserBeamR" x1="100%" y1="0%" x2="40%" y2="100%">
      <stop offset="0%" stop-color="%23ff0055" stop-opacity="0.35" />
      <stop offset="100%" stop-color="%23ff0055" stop-opacity="0" />
    </linearGradient>
    <linearGradient id="arenaFloor" x1="0%" y1="0%" x2="0%" y2="100%">
      <stop offset="0%" stop-color="%23334155" />
      <stop offset="40%" stop-color="%231e293b" />
      <stop offset="100%" stop-color="%230f172a" />
    </linearGradient>
  </defs>
  <!-- Cielo y gradas superiores -->
  <rect width="1920" height="650" fill="url(%23stadiumSky)" />
  <!-- Haces de reflectores de estadio -->
  <polygon points="150,0 700,650 350,650" fill="url(%23laserBeamL)" />
  <polygon points="1770,0 1220,650 1570,650" fill="url(%23laserBeamR)" />
  <!-- Silueta de gradas y público -->
  <path d="M0 450 Q480 400 960 410 Q1440 400 1920 450 L1920 650 L0 650 Z" fill="%230b0f19" opacity="0.8"/>
  <!-- Línea de luces de estadio -->
  <line x1="0" y1="420" x2="1920" y2="420" stroke="%2338bdf8" stroke-width="2" opacity="0.4" stroke-dasharray="10 20"/>
  <!-- Suelo de la arena / Estadio inferior -->
  <rect y="650" width="1920" height="430" fill="url(%23arenaFloor)" />
  <!-- Líneas divisorias en perspectiva -->
  <line x1="960" y1="650" x2="960" y2="1080" stroke="%23ff1744" stroke-width="4" stroke-dasharray="20 15" opacity="0.8"/>
  <ellipse cx="960" cy="850" rx="420" ry="120" fill="none" stroke="%23ffffff" stroke-width="3" opacity="0.25"/>
</svg>`;

/**
 * Fondo de Portada por defecto 1920x1080 con ambientación de estadio.
 * @type {string}
 */
export const DEFAULT_COVER_BG_SVG = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="1920" height="1080" viewBox="0 0 1920 1080">
  <defs>
    <radialGradient id="covGlow" cx="50%" cy="50%" r="60%">
      <stop offset="0%" stop-color="%231e1b4b" stop-opacity="0.95" />
      <stop offset="60%" stop-color="%230f172a" stop-opacity="0.98" />
      <stop offset="100%" stop-color="%23020617" stop-opacity="1" />
    </radialGradient>
  </defs>
  <rect width="1920" height="1080" fill="url(%23covGlow)" />
  <polygon points="0,0 960,1080 760,1080" fill="%2300f0ff" opacity="0.08" />
  <polygon points="1920,0 960,1080 1160,1080" fill="%23ff0055" opacity="0.08" />
</svg>`;

// =========================================================================
// MOTOR DE AUDIO FX (WEB AUDIO API PROCEDURAL CON TOGGLE MUTE)
// =========================================================================

/**
 * Clase Singleton para la síntesis de efectos de sonido Web Audio en Estira y Afloja.
 * Genera sonidos procedurales en tiempo real sin requerir archivos .mp3 externos.
 * Soporta control de Mute maestro (silenciado por defecto para cumplir con políticas de estadio).
 */
class TugOfWarAudioSynthEngine {
    constructor() {
        /** @type {AudioContext|null} */
        this.ctx = null;
        /** @type {GainNode|null} */
        this.masterGain = null;
        /** @type {boolean} */
        this.muted = true; // Silenciado por defecto
    }

    /**
     * Comprueba si Web Audio API está soportada.
     * @returns {boolean} True si el navegador soporta AudioContext.
     */
    isSupported() {
        return typeof window !== 'undefined' && Boolean(window.AudioContext || window.webkitAudioContext);
    }

    /**
     * Inicializa o retorna la instancia activa de AudioContext.
     * @returns {AudioContext|null} Instancia activa de AudioContext.
     */
    getAudioContext() {
        if (!this.isSupported()) return null;

        if (!this.ctx) {
            const AudioCtxClass = window.AudioContext || window.webkitAudioContext;
            this.ctx = new AudioCtxClass();
            this.masterGain = this.ctx.createGain();
            this.masterGain.gain.setValueAtTime(this.muted ? 0.0 : 0.7, this.ctx.currentTime);
            this.masterGain.connect(this.ctx.destination);
        }

        return this.ctx;
    }

    /**
     * Desbloquea el AudioContext si está suspendido.
     * @returns {Promise<boolean>} True si el AudioContext está listo.
     */
    async unlock() {
        try {
            const ctx = this.getAudioContext();
            if (!ctx) return false;
            if (ctx.state === 'suspended') {
                await ctx.resume();
            }
            return ctx.state === 'running';
        } catch (e) {
            return false;
        }
    }

    /**
     * Configura el estado de silencio maestro.
     * @param {boolean} isMuted - True para silenciar, False para activar sonido.
     */
    setMuted(isMuted) {
        this.muted = Boolean(isMuted);
        const ctx = this.getAudioContext();
        if (ctx && this.masterGain) {
            const targetVolume = this.muted ? 0.0 : 0.7;
            this.masterGain.gain.setValueAtTime(targetVolume, ctx.currentTime);
        }
    }

    /**
     * Retorna si el motor de audio está silenciado.
     * @returns {boolean} Estado de mute.
     */
    isMuted() {
        return this.muted;
    }

    /**
     * Reproduce el tick de cuenta regresiva (3, 2, 1).
     * @param {number} [freq=880] - Frecuencia del bip en Hz.
     */
    async playTick(freq = 880) {
        if (this.muted) return;
        const ctx = this.getAudioContext();
        if (!ctx) return;
        await this.unlock();
        if (ctx.state !== 'running') return;

        const now = ctx.currentTime;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, now);
        osc.frequency.exponentialRampToValueAtTime(freq * 0.7, now + 0.12);

        gain.gain.setValueAtTime(0.001, now);
        gain.gain.exponentialRampToValueAtTime(0.4, now + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.12);

        osc.connect(gain);
        gain.connect(this.masterGain || ctx.destination);

        osc.start(now);
        osc.stop(now + 0.13);
    }

    /**
     * Reproduce la corneta o silbato de inicio de partida (¡GO!).
     */
    async playGoHorn() {
        if (this.muted) return;
        const ctx = this.getAudioContext();
        if (!ctx) return;
        await this.unlock();
        if (ctx.state !== 'running') return;

        const now = ctx.currentTime;
        const frequencies = [523.25, 659.25, 783.99, 1046.5]; // C5, E5, G5, C6 (Acorde Mayor brillante)

        frequencies.forEach((freq, idx) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();

            osc.type = 'sawtooth';
            osc.frequency.setValueAtTime(freq, now);

            gain.gain.setValueAtTime(0.001, now);
            gain.gain.exponentialRampToValueAtTime(0.18 / (idx + 1), now + 0.05);
            gain.gain.linearRampToValueAtTime(0.12 / (idx + 1), now + 0.35);
            gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.6);

            osc.connect(gain);
            gain.connect(this.masterGain || ctx.destination);

            osc.start(now);
            osc.stop(now + 0.62);
        });
    }

    /**
     * Reproduce el crujido de cuerda en tensión según la intensidad de jaloneo.
     * @param {number} [intensity=0.5] - Intensidad del esfuerzo (0.0 a 1.0).
     */
    async playRopeTension(intensity = 0.5) {
        if (this.muted) return;
        const ctx = this.getAudioContext();
        if (!ctx) return;
        await this.unlock();
        if (ctx.state !== 'running') return;

        const now = ctx.currentTime;
        const normIntensity = Math.max(0.1, Math.min(1.0, intensity));

        // Ruido filtrado para simular fibras de cuerda estirándose
        const bufferSize = ctx.sampleRate * 0.15;
        const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
        const data = buffer.getChannelData(0);
        for (let i = 0; i < bufferSize; i++) {
            data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (bufferSize * 0.4));
        }

        const noise = ctx.createBufferSource();
        noise.buffer = buffer;

        const filter = ctx.createBiquadFilter();
        filter.type = 'bandpass';
        filter.frequency.setValueAtTime(300 + normIntensity * 600, now);
        filter.Q.setValueAtTime(4.0, now);

        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0.001, now);
        gain.gain.linearRampToValueAtTime(0.25 * normIntensity, now + 0.03);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.15);

        noise.connect(filter);
        filter.connect(gain);
        gain.connect(this.masterGain || ctx.destination);

        noise.start(now);
        noise.stop(now + 0.16);
    }

    /**
     * Reproduce el chapoteo de caída en lodo (*splat*).
     */
    async playMudSplash() {
        if (this.muted) return;
        const ctx = this.getAudioContext();
        if (!ctx) return;
        await this.unlock();
        if (ctx.state !== 'running') return;

        const now = ctx.currentTime;

        // 1. Sweep descendente para el impacto viscoso
        const osc = ctx.createOscillator();
        const oscGain = ctx.createGain();

        osc.type = 'sine';
        osc.frequency.setValueAtTime(220, now);
        osc.frequency.exponentialRampToValueAtTime(45, now + 0.4);

        oscGain.gain.setValueAtTime(0.001, now);
        oscGain.gain.exponentialRampToValueAtTime(0.5, now + 0.04);
        oscGain.gain.exponentialRampToValueAtTime(0.001, now + 0.4);

        osc.connect(oscGain);
        oscGain.connect(this.masterGain || ctx.destination);
        osc.start(now);
        osc.stop(now + 0.42);

        // 2. Ruido filtrado para la salpicadura líquida
        const bufferSize = ctx.sampleRate * 0.35;
        const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
        const data = buffer.getChannelData(0);
        for (let i = 0; i < bufferSize; i++) {
            data[i] = (Math.random() * 2 - 1) * Math.sin((i / bufferSize) * Math.PI);
        }

        const noise = ctx.createBufferSource();
        noise.buffer = buffer;

        const filter = ctx.createBiquadFilter();
        filter.type = 'lowpass';
        filter.frequency.setValueAtTime(800, now);
        filter.frequency.linearRampToValueAtTime(180, now + 0.35);

        const noiseGain = ctx.createGain();
        noiseGain.gain.setValueAtTime(0.001, now);
        noiseGain.gain.linearRampToValueAtTime(0.35, now + 0.05);
        noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.35);

        noise.connect(filter);
        filter.connect(noiseGain);
        noiseGain.connect(this.masterGain || ctx.destination);

        noise.start(now);
        noise.stop(now + 0.36);
    }

    /**
     * Reproduce la fanfarria de victoria y ovación triunfal.
     */
    async playVictoryCheer() {
        if (this.muted) return;
        const ctx = this.getAudioContext();
        if (!ctx) return;
        await this.unlock();
        if (ctx.state !== 'running') return;

        const now = ctx.currentTime;
        // Arpegio mayor ascendente: C4, E4, G4, C5, E5, G5
        const melody = [
            { note: 261.63, time: 0.0, dur: 0.15 },
            { note: 329.63, time: 0.15, dur: 0.15 },
            { note: 392.00, time: 0.30, dur: 0.15 },
            { note: 523.25, time: 0.45, dur: 0.30 },
            { note: 659.25, time: 0.75, dur: 0.20 },
            { note: 783.99, time: 0.95, dur: 0.70 }
        ];

        melody.forEach(({ note, time, dur }) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();

            osc.type = 'triangle';
            osc.frequency.setValueAtTime(note, now + time);

            gain.gain.setValueAtTime(0.001, now + time);
            gain.gain.exponentialRampToValueAtTime(0.3, now + time + 0.03);
            gain.gain.exponentialRampToValueAtTime(0.001, now + time + dur);

            osc.connect(gain);
            gain.connect(this.masterGain || ctx.destination);

            osc.start(now + time);
            osc.stop(now + time + dur + 0.02);
        });
    }
}

export const TugOfWarAudioSynth = new TugOfWarAudioSynthEngine();

export default {
    resolveAssetUrl,
    getResolvedMediaUrl,
    preloadImageUrls,
    DEFAULT_ROPE_SVG,
    DEFAULT_ROPE_FLAG_SVG,
    DEFAULT_MUD_PUDDLE_SVG,
    DEFAULT_PLAYER1_PHASE1_SVG,
    DEFAULT_PLAYER1_PHASE2_SVG,
    DEFAULT_PLAYER1_PHASE3_SVG,
    DEFAULT_PLAYER1_LOSER_SVG,
    DEFAULT_PLAYER1_WINNER_SVG,
    DEFAULT_PLAYER2_PHASE1_SVG,
    DEFAULT_PLAYER2_PHASE2_SVG,
    DEFAULT_PLAYER2_PHASE3_SVG,
    DEFAULT_PLAYER2_LOSER_SVG,
    DEFAULT_PLAYER2_WINNER_SVG,
    DEFAULT_TUG_BG_SVG,
    DEFAULT_COVER_BG_SVG,
    TugOfWarAudioSynth
};
